// WordPress supplies data only. Invalid fields retain their local defaults.
const isObject = value => value !== null && typeof value === 'object' && !Array.isArray(value);
const amount = value => typeof value === 'number' && Number.isFinite(value) && value >= 0;
const categories = ['main_outsourcing', 'ecosystem_sst', 'ecosystem_rrhh', 'projects'];
const types = ['onetime_fixed', 'monthly_fixed', 'risk_fixed'];
const risks = [1, 2, 3, 4, 5];
export const DEFAULT_COUPONS = [5, 10, 15, 20, 25, 30, 35, 40].map(percent => ({
    code: `xanji${percent}`, discount: percent / 100, active: true, serviceIds: []
}));

function servicePatch(raw) {
    if (!isObject(raw)) return {};
    const patch = {};
    ['title', 'desc', 'unit'].forEach(key => {
        if (typeof raw[key] === 'string' && raw[key].trim()) patch[key] = raw[key];
    });
    if (typeof raw.active === 'boolean') patch.active = raw.active;
    if (amount(raw.price)) patch.price = raw.price;
    if (Number.isFinite(raw.order)) patch.order = raw.order;
    if (Array.isArray(raw.details) && raw.details.every(item => typeof item === 'string')) patch.details = [...raw.details];
    if (isObject(raw.riskPrices)) {
        patch.riskPrices = {};
        risks.forEach(risk => { if (amount(raw.riskPrices[risk])) patch.riskPrices[risk] = raw.riskPrices[risk]; });
    }
    return patch;
}

function dateBoundary(value, end = false) {
    if (value == null) return end ? Infinity : -Infinity;
    if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}(?:T.*)?$/.test(value)) return NaN;
    const calendarDate = new Date(`${value.slice(0, 10)}T00:00:00Z`);
    if (!Number.isFinite(calendarDate.getTime()) || calendarDate.toISOString().slice(0, 10) !== value.slice(0, 10)) return NaN;
    const date = new Date(value.length === 10 ? `${value}T${end ? '23:59:59.999' : '00:00:00'}` : value);
    return date.getTime();
}

export function couponIsValid(coupon, now = Date.now()) {
    return !!coupon && coupon.active !== false && now >= dateBoundary(coupon.startDate) && now <= dateBoundary(coupon.endDate, true);
}

export function mergePricingConfig(defaults, external) {
    const config = isObject(external) ? external : {};
    const services = defaults.services.map(service => {
        const legacy = config.servicios?.[service.id];
        const legacyPrice = (typeof legacy === 'number' || (typeof legacy === 'string' && legacy.trim())) && amount(Number(legacy)) ? { price: Number(legacy) } : {};
        const patch = servicePatch(config.servicios_config?.[service.id]);
        return { ...service, details: service.details ? [...service.details] : undefined, ...legacyPrice, ...patch,
            ...(service.riskPrices ? { riskPrices: { ...service.riskPrices, ...patch.riskPrices } } : {}) };
    });
    const ids = new Set(services.map(service => service.id));
    if (Array.isArray(config.servicios_custom)) config.servicios_custom.forEach(raw => {
        if (!isObject(raw) || typeof raw.id !== 'string' || !/^[a-zA-Z0-9_-]+$/.test(raw.id) || ['__proto__', 'constructor', 'prototype'].includes(raw.id) || ids.has(raw.id) ||
            !categories.includes(raw.category) || !types.includes(raw.type) ||
            !['title', 'desc', 'unit'].every(key => typeof raw[key] === 'string' && raw[key].trim()) ||
            (raw.details !== undefined && (!Array.isArray(raw.details) || !raw.details.every(item => typeof item === 'string'))) ||
            (raw.allowQuantity !== undefined && typeof raw.allowQuantity !== 'boolean') ||
            (raw.active !== undefined && typeof raw.active !== 'boolean') ||
            (raw.type === 'risk_fixed' ? !isObject(raw.riskPrices) || !risks.every(risk => amount(raw.riskPrices[risk])) : !amount(raw.price))) return;
        ids.add(raw.id);
        const patch = servicePatch(config.servicios_config?.[raw.id]);
        services.push({ id: raw.id, category: raw.category, type: raw.type, price: 0, isCustom: true,
            ...servicePatch(raw), ...patch,
            ...(raw.type === 'risk_fixed' ? { riskPrices: { ...servicePatch(raw).riskPrices, ...patch.riskPrices } } : {}),
            allowQuantity: raw.allowQuantity === true,
            color: raw.category === 'ecosystem_sst' ? 'yellow' : ['cyan', 'purple', 'green', 'pink', 'indigo', 'rose', 'yellow'].includes(raw.color) ? raw.color : 'purple',
            icon: defaults.services[0].icon });
    });
    // Stable order: unspecified entries use their original position in their category.
    const positions = {};
    const ordered = services.map(service => {
        const index = positions[service.category] || 0;
        positions[service.category] = index + 1;
        return { service, order: service.order ?? index };
    }).sort((a, b) => categories.indexOf(a.service.category) - categories.indexOf(b.service.category) || a.order - b.order)
        .map(item => item.service).filter(service => service.active !== false);
    const tables = {};
    Object.entries(defaults.tables).forEach(([key, local]) => {
        const supplied = config.tablas?.[key];
        if (Array.isArray(local)) {
            const valid = Array.isArray(supplied) && supplied.length > 0 && supplied.every((row, index) =>
                isObject(row) && amount(row.min) && amount(row.max) && row.max >= row.min && amount(row.unitPrice) &&
                (index === 0 ? row.min === 0 : row.min === supplied[index - 1].max + 1));
            tables[key] = (valid ? supplied : local).map(row => ({ ...row }));
        } else {
            tables[key] = { ...local };
            risks.forEach(risk => { if (amount(supplied?.[risk]) && supplied[risk] > 0) tables[key][risk] = supplied[risk]; });
        }
    });
    const coupons = new Map(DEFAULT_COUPONS.map(coupon => [coupon.code, { ...coupon, serviceIds: [] }]));
    if (Array.isArray(config.cupones)) config.cupones.forEach(raw => {
        if (!isObject(raw) || typeof raw.code !== 'string' || !raw.code.trim()) return;
        const code = raw.code.trim().toLowerCase();
        const valid = amount(raw.discount) && raw.discount > 0 && raw.discount <= 1 &&
            (raw.active === undefined || typeof raw.active === 'boolean') &&
            (raw.serviceIds === undefined || (Array.isArray(raw.serviceIds) && raw.serviceIds.every(id => typeof id === 'string'))) &&
            !Number.isNaN(dateBoundary(raw.startDate)) && !Number.isNaN(dateBoundary(raw.endDate, true)) &&
            dateBoundary(raw.startDate) <= dateBoundary(raw.endDate, true);
        if (valid) coupons.set(code, { code, discount: raw.discount, active: raw.active !== false,
            serviceIds: [...(raw.serviceIds || [])], startDate: raw.startDate, endDate: raw.endDate });
    });
    const result = { ...defaults, services: ordered, tables, coupons: [...coupons.values()] };
    [['iva', 'ivaRate', 1], ['recargo_tiempo', 'timeSurcharge', Infinity], ['descuento_pack', 'bundleDiscount', 1]].forEach(([key, target, max]) => {
        const value = config[key];
        if ((typeof value === 'number' || (typeof value === 'string' && value.trim())) && amount(Number(value)) && Number(value) <= max) result[target] = Number(value);
    });
    return result;
}
